import { notification } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { notificationOptions } from "../../constants/notification.config.ts"
import type { LeadImportPreviewRow } from "../../containers/Leads/api/services.types.ts"

interface PendingImport {
  file: File
  rows: LeadImportPreviewRow[]
}

interface LeadImportThunks {
  preview: (file: File) => { unwrap: () => Promise<unknown> }
  runImport: (file: File) => { unwrap: () => Promise<unknown> }
  onImported: () => void
}

/**
 * Upload a spreadsheet: check what it would collide with, then import.
 *
 * The check is advisory — a lookup that cannot run must never cost the user
 * the file. A *refused* file is different: the import would refuse it too, so
 * it is reported once here and nothing is uploaded.
 */
function useLeadImport({ preview, runImport, onImported }: LeadImportThunks) {
  const { t } = useTranslation()
  const [pending, setPending] = useState<PendingImport | null>(null)
  const [importing, setImporting] = useState(false)

  const doImport = async (file: File) => {
    setImporting(true)
    try {
      await runImport(file).unwrap()
      onImported()
    } catch {
      // The thunk has already reported the failure; rethrowing would only
      // reach `ImportButton`'s try/finally, which has no catch of its own.
    } finally {
      setImporting(false)
    }
  }

  const start = async (file: File) => {
    try {
      const answer = (await preview(file).unwrap()) as {
        rows?: LeadImportPreviewRow[]
      }
      const rows = answer?.rows ?? []
      if (rows.some((row) => row.matches.length > 0)) {
        setPending({ file, rows })
        return
      }
    } catch (rejection) {
      const refusal = rejection as { status?: number | null; data?: unknown }
      if (refusal?.status === 400) {
        notification.error({
          ...notificationOptions.error,
          message: t("leadImportPreview.refusedTitle"),
          description:
            (refusal.data as { detail?: string })?.detail ??
            t("leadImportPreview.refusedBody")
        })
        return
      }

      // An unchecked import that looks like a checked one is worse than no
      // check at all, so the skip is announced rather than silent.
      notification.warning({
        ...notificationOptions.warning,
        message: t("leadImportPreview.skippedTitle"),
        description: t("leadImportPreview.skippedBody")
      })
    }

    await doImport(file)
  }

  const confirm = async () => {
    if (!pending) return
    // The report stays up, showing progress, until the import has answered —
    // closing on the click leaves the page looking idle mid-upload. It closes
    // either way: one left open after a failure is indistinguishable from one
    // never confirmed.
    await doImport(pending.file)
    setPending(null)
  }

  return {
    pending,
    importing,
    start,
    confirm,
    cancel: () => setPending(null)
  }
}

export { type PendingImport, useLeadImport }
